import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Users, Car, CalendarDays, ShieldCheck, CheckCircle2, ArrowRight, BadgeCheck } from 'lucide-react';
import { estimatePrice, estimateRange, VEHICLE_META } from '../../utils/priceCalculator';
import { buildBookingState } from '../../utils/bookingLink';

/**
 * QuotePreviewWidget — CRO price-preview widget for SEO route landing pages.
 *
 * Marketplace-conform copy: NO "Fixpreis". Always "Fahrer bieten" / "ab €X".
 * Prefills the booking flow via React Router state (BookingDetails.js
 * already reads location.state on mount — no BookingDetails change required).
 *
 * Props:
 *   route: seoConfig ROUTES entry (must include fromCoords + toCoords)
 *   activeDriverCount: number (trust signal; may be static)
 */
export function QuotePreviewWidget({ route, activeDriverCount = 50 }) {
  const navigate = useNavigate();
  const [passengers, setPassengers] = useState(2);
  const [vehicleId, setVehicleId] = useState('comfort');
  // Default pickup date: tomorrow.
  const [pickupDate, setPickupDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  });

  const distanceKm = route?.km || 0;

  // Live-recalc — no API calls.
  const range = useMemo(() => estimateRange(distanceKm), [distanceKm]);
  const selectedPrice = useMemo(() => estimatePrice(vehicleId, distanceKm), [vehicleId, distanceKm]);

  const track = (event, extra = {}) => {
    console.log('quote_widget', { event, route: `${route.fromSlug}--${route.toSlug}`, ...extra });
  };

  const handleCTA = () => {
    const state = buildBookingState(route);
    if (!state) return;
    track('booking_flow_initiated', {
      passengers, vehicleId, pickupDate,
      estimated_low: range.low, estimated_high: range.high,
    });
    navigate('/booking', { state });
  };

  const vehicleOptions = VEHICLE_META.filter(v => v.pax >= passengers).slice(0, 8);
  const durationLabel = `${Math.floor((route?.min || 0) / 60)}h ${(route?.min || 0) % 60}m`;

  // Pre-computed option arrays via React.createElement — the inline .map()
  // approach is wrapped with <span> by the preview-only Emergent instrumentation
  // and produces "<span> cannot be child of <select>" hydration warnings.
  const passengerOptions = useMemo(
    () => [1,2,3,4,5,6,7,8].map(n => React.createElement('option', { key: n, value: n }, `${n} Personen`)),
    []
  );
  const vehicleOptionElements = useMemo(
    () => vehicleOptions.map(v => React.createElement('option', { key: v.id, value: v.id }, `${v.labelDe} · ${v.pax} Pers.`)),
    [vehicleOptions]
  );

  return (
    <aside
      className="bg-white rounded-2xl border border-slate-200 shadow-lg p-6 relative overflow-hidden"
      data-testid="quote-widget"
    >
      {/* Trust badge top-right */}
      <div className="absolute top-4 right-4 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold" data-testid="quote-widget-driver-count">
        <BadgeCheck size={11} />
        {activeDriverCount}+ verifizierte Fahrer
      </div>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={18} className="text-orange-500" />
        <h3 className="text-lg font-bold text-slate-900">Preis-Vorschau: Fahrer bieten für deine Route</h3>
      </div>
      <p className="text-xs text-slate-500 mb-5">Individuelle Angebote in unter 5 Minuten</p>

      {/* Route summary (prefilled) */}
      <div className="rounded-lg bg-slate-50 p-3 mb-4 text-sm" data-testid="quote-widget-route">
        <div className="flex items-start gap-2">
          <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase text-slate-400 tracking-wide">Von</p>
            <p className="font-medium text-slate-800 truncate">{route.from}</p>
          </div>
        </div>
        <div className="ml-[3px] my-1 border-l-2 border-dotted border-slate-300 h-3" />
        <div className="flex items-start gap-2">
          <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase text-slate-400 tracking-wide">Nach</p>
            <p className="font-medium text-slate-800 truncate">{route.to}</p>
          </div>
        </div>
        <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-3 text-[11px] text-slate-500">
          <span>{route.km} km</span>
          <span>·</span>
          <span>{durationLabel} Fahrzeit</span>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="block">
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600 mb-1"><CalendarDays size={11} /> Datum</span>
          <input
            type="date"
            value={pickupDate}
            onChange={(e) => { setPickupDate(e.target.value); track('quote_calculated', { field: 'date' }); }}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-orange-400 focus:outline-none"
            data-testid="quote-widget-date"
          />
        </label>
        <label className="block">
          <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600 mb-1"><Users size={11} /> Passagiere</span>
          <select
            value={passengers}
            onChange={(e) => { setPassengers(Number(e.target.value)); track('quote_calculated', { field: 'passengers' }); }}
            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-orange-400 focus:outline-none"
            data-testid="quote-widget-passengers"
          >
            {passengerOptions}
          </select>
        </label>
      </div>
      <label className="block mb-4">
        <span className="flex items-center gap-1.5 text-[11px] font-medium text-slate-600 mb-1"><Car size={11} /> Fahrzeugtyp</span>
        <select
          value={vehicleId}
          onChange={(e) => { setVehicleId(e.target.value); track('quote_calculated', { field: 'vehicle' }); }}
          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:border-orange-400 focus:outline-none"
          data-testid="quote-widget-vehicle"
        >
          {vehicleOptionElements}
        </select>
      </label>

      {/* Price range */}
      <div className="rounded-lg bg-gradient-to-r from-orange-50 to-emerald-50 border border-orange-100 p-4 mb-4" data-testid="quote-widget-price">
        <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Preisschätzung</p>
        <p className="text-2xl font-extrabold text-slate-900">
          ab €{range.low} <span className="text-base font-medium text-slate-500">– bis €{range.high}</span>
        </p>
        <p className="text-xs text-slate-600 mt-1">
          Deine Auswahl (<span className="font-medium">{VEHICLE_META.find(v => v.id === vehicleId)?.labelDe}</span>): <span className="font-bold text-slate-800">ab €{selectedPrice}</span>
        </p>
        <p className="text-[10px] text-slate-500 mt-2 italic">
          Endgültige Preise werden von verifizierten Fahrern angeboten. Schätzung basierend auf Distanz und Fahrzeugtyp.
        </p>
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={handleCTA}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-bold text-sm shadow-md transition-colors"
        data-testid="quote-widget-cta"
      >
        🚀 Individuelle Angebote anfordern
        <ArrowRight size={16} />
      </button>

      {/* Trust bullets */}
      <ul className="mt-4 space-y-1.5 text-[11px] text-slate-600">
        {[
          'Kostenlose Stornierung bis 24 h vor Abfahrt',
          'Meet & Greet am Terminal inklusive',
          'Fahrer wartet bei Flugverspätung kostenlos',
          'Kindersitze auf Anfrage',
          'Zahlung sicher über 366transfer',
        ].map((t) => (
          <li key={t} className="flex items-start gap-1.5">
            <CheckCircle2 size={11} className="text-emerald-500 mt-0.5 shrink-0" />
            <span>{t}</span>
          </li>
        ))}
      </ul>

      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-1.5 text-[10px] text-slate-500">
        <ShieldCheck size={11} className="text-emerald-500" />
        <span>Marketplace-Prinzip: Du wählst aus mehreren Angeboten – keine erzwungene Preisvorgabe.</span>
      </div>
    </aside>
  );
}
